import { useCan } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { Group } from '@/services/groupsManagement/groupsManagement.types';
import * as React from 'react';
import { useUpdateGroupStatus } from '../../hooks/useUpdateGroupStatus';
import ManageGroupsFormContainer from '../../ManageGroupsFormContainer';
import { getColumns } from './Column';

export function GroupsTable({
  data,
  pagination,
  isLoading,
}: {
  data: Group[];
  pagination: Pagination;
  isLoading: boolean;
}) {
  const { t } = useDynamicLocale();
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync } = useUpdateGroupStatus();
  const canAddNew = useCan({ allOf: [PERMS.group.manage.group] });

  const columns = React.useMemo(
    () => getColumns(onOpen, t, openConfirmation, mutateAsync),
    [onOpen, t, openConfirmation, mutateAsync],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Add New Group',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'configurationManagement.groupsManagement.manageGroups.title',
      component: <ManageGroupsFormContainer />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'groupId', desc: true }],
    },
    meta: {
      title: <CrumbTitle id="configurationManagement.page.table.group" />,
      actionKey: 'onAddNew',
      action: canAddNew ? onAddNew : undefined,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.groupId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
